import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useApiClient } from "@src/packages/api/louise-api";
import {
  getUserClinicInteraction,
  upsertUserClinicInteraction,
} from "../clinics.api";
import { clinicsConfig } from "../clinics.config";
import type { UserClinicInteraction } from "../clinics.types";
import { useTranslations } from "@src/packages/i18n";
import { useToast } from "@louise/moon-ui";
import {
  createClinicSaveCoordinator,
  type ClinicSaveState,
} from "./clinicInteractionSave";
import {
  encodeClinicNotesField,
  parseClinicNotesField,
} from "./clinicNotesEncoding";
import type { ClinicJourneyStatus } from "./clinicNotesEncoding";

export {
  encodeClinicNotesField,
  parseClinicNotesField,
} from "./clinicNotesEncoding";
export type { ClinicJourneyStatus } from "./clinicNotesEncoding";

export function getUserInteractionKey(clinicId: string) {
  return [clinicsConfig.key, clinicId, clinicsConfig.userInteractionKey];
}

export function useClinicUserInteraction(clinicId: string, userId: string) {
  const { client, tenantId } = useApiClient();

  const query = useQuery<UserClinicInteraction | null>({
    enabled: !!userId && !!tenantId,
    queryFn: () => getUserClinicInteraction(client, tenantId, clinicId),
    queryKey: getUserInteractionKey(clinicId),
  });

  return query;
}

export function useClinicNotes(
  clinicId: string,
  userId: string,
  userInteraction?: UserClinicInteraction | null,
) {
  const { client, tenantId } = useApiClient();
  const queryClient = useQueryClient();
  const t = useTranslations();
  const { showToast } = useToast();

  const coordinatorRef = useRef<{
    clinicId: string;
    coordinator: ReturnType<typeof createClinicSaveCoordinator<string>>;
  } | null>(null);
  const showSaveFailureToast = useCallback(
    (status: "failed" | "conflict") => {
      showToast({
        variant: "error",
        message: t(
          status === "conflict"
            ? "access.clinic_detail.journey.conflict_message"
            : "access.clinic_detail.journey.save_failed_message",
        ),
      });
    },
    [showToast, t],
  );

  const showSaveSuccessToast = useCallback(() => {
    showToast({
      variant: "info",
      message: t("access.clinic_detail.journey.saved"),
      duration: 1400,
    });
  }, [showToast, t]);

  const rawNotes = userInteraction?.notes || "";
  const parsed = parseClinicNotesField(rawNotes);

  const mutation = useMutation({
    mutationFn: async (notes: string) => {
      const result = await upsertUserClinicInteraction(
        client,
        tenantId,
        clinicId,
        {
          notes,
          isLiked: userInteraction?.isLiked ?? false,
        },
      );
      return result;
    },
    onSuccess: (result) => {
      // The cache is updated only after the request has completed. This keeps
      // a refetch from looking like confirmation while the mutation is still
      // unresolved, and lets the coordinator reconcile the exact response.
      queryClient.setQueryData(getUserInteractionKey(clinicId), result);
      queryClient.invalidateQueries({
        queryKey: getUserInteractionKey(clinicId),
      });
    },
    onError: () => {
      // Do not include the note, status, or API error in telemetry/logs.
      console.error("Failed to save clinic interaction");
    },
  });

  const mutationRef = useRef(mutation);
  mutationRef.current = mutation;

  const updateSaveStateRef = useRef<(state: ClinicSaveState<string>) => void>(
    () => undefined,
  );
  if (!coordinatorRef.current || coordinatorRef.current.clinicId !== clinicId) {
    const coordinator = createClinicSaveCoordinator<string>({
      initialValue: rawNotes,
      equals: (left, right) => left === right,
      save: async (notes) => {
        const result = await mutationRef.current.mutateAsync(notes);
        return result.notes ?? notes;
      },
      onChange: (state) => {
        updateSaveStateRef.current(state);
        if (state.status === "failed") showSaveFailureToast("failed");
        if (state.status === "confirmed") showSaveSuccessToast();
        if (
          state.draft === draftEncodedRef.current ||
          state.hasUnresolvedDraft
        ) {
          return;
        }
        const nextDraft = parseClinicNotesField(state.draft);
        draftEncodedRef.current = state.draft;
        noteRef.current = nextDraft.text;
        statusRef.current = nextDraft.status;
        setNote(nextDraft.text);
        setStatusState(nextDraft.status);
      },
    });
    coordinatorRef.current = { clinicId, coordinator };
  }

  const coordinator = coordinatorRef.current.coordinator;

  const [note, setNote] = useState(parsed.text);
  const [status, setStatusState] = useState<ClinicJourneyStatus | null>(
    parsed.status,
  );
  const noteRef = useRef(parsed.text);
  const statusRef = useRef<ClinicJourneyStatus | null>(parsed.status);
  const draftEncodedRef = useRef(rawNotes);
  const conflictServerRef = useRef<string | null>(null);
  const [saveState, setSaveState] = useState<ClinicSaveState<string>>(() =>
    coordinator.getState(),
  );
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  updateSaveStateRef.current = setSaveState;

  useEffect(() => {
    const previousDraft = draftEncodedRef.current;
    const nextState = coordinator.reconcileServerValue(
      userInteraction?.notes || "",
    );
    if (nextState.status === "conflict") {
      if (conflictServerRef.current !== nextState.confirmed) {
        conflictServerRef.current = nextState.confirmed;
        showSaveFailureToast("conflict");
      }
    } else {
      conflictServerRef.current = null;
    }
    if (nextState.draft !== previousDraft) {
      const nextDraft = parseClinicNotesField(nextState.draft);
      draftEncodedRef.current = nextState.draft;
      noteRef.current = nextDraft.text;
      statusRef.current = nextDraft.status;
      setNote(nextDraft.text);
      setStatusState(nextDraft.status);
    }
  }, [coordinator, showSaveFailureToast, userInteraction?.notes]);

  const setNoteAndSave = (value: string) => {
    noteRef.current = value;
    setNote(value);
    const encoded = encodeClinicNotesField(statusRef.current, value);
    draftEncodedRef.current = encoded;
    coordinator.updateDraft(encoded);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      coordinator.saveDraft(encoded);
    }, 800);
  };

  const setStatus = (s: ClinicJourneyStatus | null) => {
    statusRef.current = s;
    setStatusState(s);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const encoded = encodeClinicNotesField(s, noteRef.current);
    draftEncodedRef.current = encoded;
    coordinator.saveDraft(encoded);
  };

  const handleBlur = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const encoded = encodeClinicNotesField(statusRef.current, noteRef.current);
    draftEncodedRef.current = encoded;
    coordinator.saveDraft(encoded);
  };

  // Flush any pending debounced save on unmount, so a note left within the
  // 800ms window (e.g. the screen closes before the timer fires) isn't lost.
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (draftEncodedRef.current !== coordinator.getState().confirmed) {
        coordinator.saveDraft(draftEncodedRef.current);
      }
      coordinator.destroy();
    };
  }, [coordinator]);

  return {
    note,
    setNote: setNoteAndSave,
    status,
    setStatus,
    handleBlur,
    isLoading: saveState.status === "saving",
    saveState,
  };
}
